import { Action, isType } from "typescript-fsa";
import update from "immutability-helper";
import log from "loglevel";
import { Map } from "immutable";

import { isFailedResponseUnauthenticatedNotLogin } from "@api/actions";
import { logout } from "@auth/actions";
import {
    makeMapLoadedModel,
    makeMergeEntities,
    makeHandleShowFailure,
    PaginationResult,
} from "@orm/models";
import * as charts from "@charts/actions";
import * as deliveries from "@deliveries/actions";
import * as reports from "@reports/actions";
import * as sales from "@sales/actions";
import * as search from "@search/actions";
import * as upload from "@upload/actions";
import * as actions from "../actions";
import {
    Album,
    Track,
    Artist,
    Genre,
    Label,
    AlbumFormat,
    AlbumStatus,
    AlbumTrack,
    ArtistPivot,
    MusicNormalisedEntities,
    MusicInternalState as State,
} from "../models";

const getInitialState = () => ({
    entities: {
        albums: Map<number, Album>(),
        tracks: Map<number, Track>(),
        artists: Map<number, Artist>(),
        genres: Map<number, Genre>(),
        labels: Map<number, Label>(),
        albumFormats: Map<string, AlbumFormat>(),
        albumStatuses: Map<string, AlbumStatus>(),
        albumTracks: Map<string, AlbumTrack>(),
        artistPivots: Map<string, ArtistPivot>(),
    },
    lists: { albums: [] },
    trackMapping: Map<number, number>(),
} as State);

type MusicAction = actions.MusicAction & charts.ChartsAction;

const mapLoadedAlbum = makeMapLoadedModel<Album>("album", Album);
const mapLoadedTrack = makeMapLoadedModel<Track>("track", Track);
const mapLoadedArtist = makeMapLoadedModel<Artist>("artist", Artist);
const mergeEntities = makeMergeEntities<State, MusicNormalisedEntities>(
    [
        "albums", "tracks", "artists", "genres", "labels", "albumFormats",
        "albumStatuses", "albumTracks", "artistPivots",
    ],
    { albums: mapLoadedAlbum, tracks: mapLoadedTrack, artists: mapLoadedArtist },
);
const albumsIndexSuccess = (state: State, action: Action<[PaginationResult, actions.AlbumsResult]>) => {
    const pagination = action.payload[0];
    const { result, entities } = action.payload[1];
    // If the pagination is the first page, replace all the albums.
    const newState = update(state, {
        lists: {
            albums: { $set: (pagination.page === 1
                ? result
                : state.lists.albums.concat(result)) },
        },
    });
    return mergeEntities(newState, entities, "album");
};
const albumsShowFailure = makeHandleShowFailure<State, Album, MusicNormalisedEntities>(
    Album,
    "albums",
);
const tracksShowFailure = makeHandleShowFailure<State, Track, MusicNormalisedEntities>(
    Track,
    "tracks",
);
const artistsShowFailure = makeHandleShowFailure<State, Artist, MusicNormalisedEntities>(
    Artist,
    "artists",
);

function music(state: State = getInitialState(), action: MusicAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("music state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.albumsIndexSuccess)) {
        // Successful albumsIndex
        return albumsIndexSuccess(state, action);
    }
    else if (isType(action, actions.albumsShowSuccess)) {
        // Successful albumsShow
        return mergeEntities(state, action.payload.entities, "album");
    }
    else if (isType(action, actions.albumsShowFailure)) {
        // Failed albumsShow
        return albumsShowFailure(state, action.payload);
    }
    else if (isType(action, actions.albumsSearchSuccess)) {
        // Successful albumsSearch
        return mergeEntities(state, action.payload.entities, "album");
    }
    else if (isType(action, actions.tracksShowSuccess)) {
        // Successful tracksShow
        return mergeEntities(state, action.payload.entities, "track");
    }
    else if (isType(action, actions.tracksShowFailure)) {
        // Failed tracksShow
        return tracksShowFailure(state, action.payload);
    }
    else if (isType(action, actions.tracksSearchSuccess)) {
        // Successful tracksSearch
        return mergeEntities(state, action.payload.entities, "track");
    }
    else if (isType(action, actions.artistsShowSuccess)) {
        // Successful artistsShow
        return mergeEntities(state, action.payload.entities, "artist");
    }
    else if (isType(action, actions.artistsShowFailure)) {
        // Failed artistsShow
        return artistsShowFailure(state, action.payload);
    }
    else if (isType(action, actions.tracksShowRedirect)) {
        // Redirect trackShow
        return update(state, { trackMapping: { $set: state.trackMapping.set(action.payload[0], action.payload[1]) } });
    }
    else if (isType(action, deliveries.deliveriesIndexSuccess)) {
        // Successful deliveriesIndex
        return mergeEntities(state, action.payload[1].entities);
    }
    else if (isType(action, deliveries.deliveriesShowSuccess)) {
        // Successful deliveriesShow
        return mergeEntities(state, action.payload.entities);
    }
    else if (isType(action, upload.bundlesIndexSuccess)) {
        // Successful bundlesIndex
        return mergeEntities(state, action.payload[1].entities);
    }
    else if (isType(action, upload.bundlesShowSuccess)) {
        // Successful bundlesShow
        return mergeEntities(state, action.payload.entities);
    }
    else if (isType(action, reports.reportsIndexSuccess)) {
        // Successful reportsIndex
        return mergeEntities(state, action.payload[1].entities);
    }
    else if (isType(action, charts.chartsIndexSuccess)) {
        // Successful chartsIndex
        return mergeEntities(state, action.payload.entities);
    }
    else if (isType(action, sales.playlistTrendsGetSuccess)) {
        // Successful playlistTrendsGet
        return mergeEntities(state, action.payload[1].entities);
    }
    else if (isType(action, search.searchSuccess)) {
        // Successful search
        return mergeEntities(state, action.payload.entities);
    }
    else if (isType(action, logout) || isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

export { music, getInitialState };
